import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useLayer } from "@/lib/layer-stack";
import { createPortal } from "react-dom";
import { I } from "@/components/Icon";
import { METRICS, type MetricDef } from "@/lib/metrics";

type TriggerProps = { open: boolean; toggle: () => void };
type MultiTriggerProps = { open: boolean; toggle: () => void; count: number };

type Coords = { top: number; left: number; minWidth: number };

// Compute popover position on demand — aligned to the right edge of the
// trigger, opening downward if there's room, upward otherwise. Keeps
// the popover outside any `overflow: hidden` ancestors.
function usePopoverCoords(
  triggerRef: React.RefObject<HTMLElement>,
  open: boolean,
  desiredWidth: number
): Coords | null {
  const [coords, setCoords] = useState<Coords | null>(null);
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setCoords(null);
      return;
    }
    const update = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      const width = Math.max(desiredWidth, r.width);
      const viewportW = window.innerWidth;
      // Default: right-align the popover with the trigger.
      let left = r.right - width;
      if (left < 8) left = 8;
      if (left + width > viewportW - 8) left = viewportW - 8 - width;
      const top = r.bottom + 6;
      setCoords({ top, left, minWidth: width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, desiredWidth, triggerRef]);
  return coords;
}

export function MetricPicker({
  value,
  onChange,
  exclude = [],
  trigger,
}: {
  value: string;
  onChange: (id: string) => void;
  exclude?: string[];
  trigger: (p: TriggerProps) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const coords = usePopoverCoords(wrapRef, open, 240);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        wrapRef.current && !wrapRef.current.contains(t) &&
        popoverRef.current && !popoverRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useLayer(open, () => setOpen(false));

  const opts: MetricDef[] = METRICS.filter(
    (m) => m.id === value || !exclude.includes(m.id)
  );

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open && coords &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              minWidth: coords.minWidth,
              zIndex: 500,
              background: "var(--c-surface)",
              border: "1px solid var(--c-border)",
              borderRadius: 10,
              boxShadow: "var(--sh-3)",
              padding: 6,
              maxHeight: 360,
              overflow: "auto",
            }}
          >
            <div
              style={{
                padding: "6px 10px 4px",
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 0.4,
                color: "var(--c-text-4)",
                textTransform: "uppercase",
              }}
            >
              Show metric
            </div>
            {opts.map((m) => (
              <div
                key={m.id}
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                }}
                style={{
                  padding: "7px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: m.id === value ? "var(--c-accent-50)" : "transparent",
                  color: m.id === value ? "var(--c-accent-700)" : "var(--c-text)",
                }}
                onMouseEnter={(e) => {
                  if (m.id !== value)
                    (e.currentTarget as HTMLDivElement).style.background =
                      "var(--c-surface-sunk)";
                }}
                onMouseLeave={(e) => {
                  if (m.id !== value)
                    (e.currentTarget as HTMLDivElement).style.background = "transparent";
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: m.color,
                  }}
                />
                <span style={{ flex: 1 }}>{m.label}</span>
                {m.id === value && (
                  <span style={{ color: "var(--c-accent-600)" }}>{I.check}</span>
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </span>
  );
}

export function MultiMetricPicker({
  values,
  onChange,
  min = 1,
  max = 6,
  trigger,
  title = "Show metrics",
  width = 280,
  extraMetrics,
  presets,
  onlyMetrics,
}: {
  values: string[];
  onChange: (ids: string[]) => void;
  min?: number;
  max?: number;
  trigger: (p: MultiTriggerProps) => ReactNode;
  title?: string;
  width?: number;
  // Tile-config-driven per-app metrics (§36). Prepended to the
  // dropdown options so users can switch the chart between Spend
  // and any of the per-app slots they configured in App Settings.
  extraMetrics?: MetricDef[];
  // ★ Блок пресетов — НЕОБЯЗАТЕЛЬНЫЙ. Нет пропа — нет блока, и дропдаун
  // Overview остаётся тем же, чем был: пресеты есть только у графика
  // Ads Manager, и платить за них правкой соседнего экрана незачем.
  presets?: PresetsBlock;
  // Показывать только эти метрики. Пусто — весь реестр, как раньше.
  // График Ads Manager отдаёт сюда свой набор: реестр Overview на нём
  // предлагал бы Trials и ROAS, которых в его данных нет.
  onlyMetrics?: MetricDef[];
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const coords = usePopoverCoords(wrapRef, open, width);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        wrapRef.current && !wrapRef.current.contains(t) &&
        popoverRef.current && !popoverRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useLayer(open, () => setOpen(false));

  const toggle = (id: string) => {
    const has = values.includes(id);
    let next: string[];
    if (has) next = values.filter((v) => v !== id);
    else next = [...values, id];
    if (next.length < min) return;
    if (next.length > max) next = next.slice(next.length - max);
    onChange(next);
  };

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      {trigger({ open, toggle: () => setOpen((o) => !o), count: values.length })}
      {open && coords &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              minWidth: coords.minWidth,
              zIndex: 500,
              background: "var(--c-surface)",
              border: "1px solid var(--c-border)",
              borderRadius: 10,
              boxShadow: "var(--sh-3)",
              padding: 6,
              maxHeight: 420,
              overflow: "auto",
            }}
          >
            <div
              style={{
                padding: "6px 10px 4px",
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 0.4,
                color: "var(--c-text-4)",
                textTransform: "uppercase",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>{title}</span>
              <span style={{ color: "var(--c-text-3)" }}>
                {values.length}/{max}
              </span>
            </div>
            {(onlyMetrics ?? [...(extraMetrics ?? []), ...METRICS]).map((m) => {
              const checked = values.includes(m.id);
              const disabled = !checked && values.length >= max;
              return (
                <label
                  key={m.id}
                  style={{
                    padding: "7px 10px",
                    borderRadius: 6,
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: disabled ? 0.5 : 1,
                    background: checked ? "var(--c-accent-50)" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!checked && !disabled)
                      (e.currentTarget as HTMLLabelElement).style.background =
                        "var(--c-surface-sunk)";
                  }}
                  onMouseLeave={(e) => {
                    if (!checked)
                      (e.currentTarget as HTMLLabelElement).style.background =
                        "transparent";
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(m.id)}
                    style={{ margin: 0 }}
                  />
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: m.color,
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      color: checked ? "var(--c-accent-700)" : "var(--c-text)",
                    }}
                  >
                    {m.label}
                  </span>
                </label>
              );
            })}
            {presets ? <PresetsSection {...presets} current={values} /> : null}
          </div>,
          document.body
        )}
    </span>
  );
}

export function PencilButton({
  onClick,
  active,
}: {
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      className="hover-reveal-btn"
      onClick={onClick}
      aria-label="Configure metric"
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        border: "1px solid var(--c-border)",
        background: active ? "var(--c-accent-50)" : "var(--c-surface)",
        color: active ? "var(--c-accent-700)" : "var(--c-text-3)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        padding: 0,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path
          d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Пресеты набора метрик (график Ads Manager)
// ---------------------------------------------------------------------------

export type PresetsBlock = {
  items: { id: number; name: string; metrics: string[] }[];
  onApply: (metrics: string[]) => void;
  onDelete: (id: number) => void;
  onSave: (name: string) => void;
  saving?: boolean;
};

/**
 * Список сохранённых наборов и «Save as preset».
 *
 * ★ Живёт внутри дропдауна метрик, а не отдельной кнопкой рядом: пресет
 * — это тот же выбор метрик, только записанный. Вынеси его наружу, и
 * человеку придётся помнить, что «набор» и «пресет» — про одно и то же.
 */
function PresetsSection({
  items, onApply, onDelete, onSave, saving = false, current,
}: PresetsBlock & { current: string[] }) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  // Esc while naming only cancels the name — the picker stays open.
  useLayer(naming, () => {
    setNaming(false);
    setName("");
  });

  const commit = () => {
    const clean = name.trim();
    if (!clean) return;
    onSave(clean);
    setName("");
    setNaming(false);
  };

  return (
    <>
      <div className="metric-presets-sep" />
      <div className="metric-presets-head">Presets</div>
      {items.map((p) => (
        <div key={p.id} className="metric-preset-row">
          <button
            type="button"
            className="metric-preset-apply"
            onClick={() => onApply(p.metrics)}
            title={p.metrics.join(", ")}
          >
            {p.name}
          </button>
          <button
            type="button"
            className="metric-preset-del"
            onClick={() => onDelete(p.id)}
            title={`Delete ${p.name}`}
            aria-label={`Delete preset ${p.name}`}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <path d="M6 6l12 12" /><path d="M6 18 18 6" />
            </svg>
          </button>
        </div>
      ))}
      {naming ? (
        <div className="metric-preset-new">
          <input
            autoFocus
            value={name}
            placeholder="Preset name"
            onChange={(e) => setName(e.target.value)}
            // Enter сохраняет — иначе за именем пришлось бы тянуться
            // мышью к кнопке, стоящей в двадцати пикселях.
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
            }}
          />
          <button
            type="button"
            onClick={commit}
            // Пустое имя и сохранение в полёте — не повод для отказа с
            // сообщением: кнопка просто недоступна, пока сохранять нечего.
            disabled={!name.trim() || saving}
            aria-label="Save preset"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="m5 12 5 5 10-10" />
            </svg>
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="metric-preset-add"
          onClick={() => setNaming(true)}
          disabled={current.length === 0}
          title={
            current.length === 0
              ? "Pick at least one metric first"
              : "Save the current set under a name"
          }
        >
          + Save as preset
        </button>
      )}
    </>
  );
}
